import { css } from '@emotion/react';
import { media } from '@/theme/tokens/breakpoints';
import type { AppTheme } from '@/theme/theme';

export const createAuthStyles = (backgroundImage: string, theme?: AppTheme) => ({
  container: css`
    height: 100%;
    display: flex;
    align-items: stretch;
    width: 100%;
    max-width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  `,
  leftSection: css`
    display: none;
    ${media.lg(`
      display: flex;
      width: 33.333333%;
      background: url(${backgroundImage}) center/cover no-repeat;
      position: relative;
      overflow: hidden;
    `)}
  `,
  leftOverlay: css`
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(106, 60, 250, 0.5), rgba(75, 32, 211, 0.3));
  `,
  leftContent: css`
    position: relative;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `,
  leftText: css`
    text-align: center;
  `,
  rightSection: css`
    width: 100%;
    flex: 1;
    box-sizing: border-box;
    ${media.lg`width: 66.666667%;`}
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme?.colors.surface.layout.background};
    ${media.maxLg(`
      align-items: flex-start;
      justify-content: flex-start;
      padding-top: max(${theme?.spacing[6] ?? 16}px, env(safe-area-inset-top, 0px));
      padding-bottom: max(${theme?.spacing[6] ?? 16}px, env(safe-area-inset-bottom, 0px));
      padding-left: max(${theme?.spacing[6] ?? 16}px, env(safe-area-inset-left, 0px));
      padding-right: max(${theme?.spacing[6] ?? 16}px, env(safe-area-inset-right, 0px));
    `)}
  `,
  /** 좌측 히어로가 숨겨질 때 상단 브랜드 (모바일·태블릿) */
  mobileBrandBar: css`
    display: none;
    ${media.maxLg(`
      display: flex;
      justify-content: center;
      width: 100%;
      margin-bottom: ${theme?.spacing[4] ?? 8}px;
    `)}
  `,
  mobileLogo: css`
    width: 200px;
    max-width: min(70vw, 240px);
    height: auto;
    display: block;
  `,
  formContainer: css`
    max-width: 28rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 600px;
    justify-content: start;
    box-sizing: border-box;
    ${media.maxLg`
      min-height: 0;
      flex: 1;
    `}
  `,
  formTitle: css`
    font-size: 1.875rem;
    font-weight: bold;
    color: ${theme?.colors.text || '#111827'};
    margin-bottom: 0.5rem;
    ${media.maxXs`font-size: 1.5rem;`}
  `,
  formTitleLine: css`
    width: 3rem;
    height: 0.25rem;
    background: linear-gradient(
      to right,
      ${theme?.colors.button.bg.primary.default},
      ${theme?.colors.button.bg.primary.hover}
    );
  `,
  form: css`
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  `,
  fieldGroup: css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 320px;
    ${media.maxLg`min-height: 0;`}
  `,
  field: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    ${media.maxLg`
      input,
      textarea {
        font-size: 16px;
      }
    `}
  `,
  label: css`
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: ${theme?.colors.text.secondary};
    margin-bottom: 0.25rem;
  `,
  linkContainer: css`
    text-align: center;
    margin-top: 1rem;
  `,
  link: css`
    color: ${theme?.colors.control.timetrack.progress};
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  `,
  divider: css`
    display: flex;
    align-items: center;
    margin: 1.5rem 0;
    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${theme?.colors.border};
    }
  `,
  dividerText: css`
    padding: 0 1rem;
    color: ${theme?.colors.text.secondary};
    font-size: 0.875rem;
  `,
  secondaryButton: css`
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid ${theme?.colors.border};
    border-radius: 0.375rem;
    background: ${theme?.colors.surface.layout.container};
    color: ${theme?.colors.text.secondary};
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    &:hover {
      background: ${theme?.colors.surface.interactive.hover};
      border-color: ${theme?.colors.border};
    }
    &:focus {
      outline: none;
      ring: 2px;
      ring-color: ${theme?.colors.control.timetrack.progress};
    }
  `,
  checkboxContainer: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
  `,
  checkbox: css`
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    border: 2px solid ${theme?.colors.border};
    border-radius: 0.25rem;
    background: ${theme?.colors.surface.layout.container};
    cursor: pointer;
    ${media.maxLg`
      width: 1.25rem;
      height: 1.25rem;
    `}
    &:checked {
      background: ${theme?.colors.control.timetrack.progress};
      border-color: ${theme?.colors.control.timetrack.progress};
    }
    &:focus {
      outline: none;
      ring: 2px;
      ring-color: ${theme?.colors.control.timetrack.progress};
    }
  `,
  checkboxLabel: css`
    font-size: 0.875rem;
    color: ${theme?.colors.text.primary};
    cursor: pointer;
    user-select: none;
  `,
  checkboxGroup: css`
    display: flex;
    align-items: center;
  `,

  bottomLinks: css`
    margin-top: 1rem;
    text-align: center;
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-height: 0;
    ${media.lg`min-height: 60px;`}
  `,
  linkText: css`
    font-size: 0.875rem;
    color: ${theme?.colors.text.secondary};
  `,
  // Modal styles
  modalOverlay: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  `,
  modal: css`
    background: ${theme?.colors.surface.layout.container};
    border-radius: 0.5rem;
    padding: 2rem;
    max-width: 600px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 10px 10px -5px rgba(0, 0, 0, 0.04);
  `,
  modalTitle: css`
    font-size: 1.5rem;
    font-weight: bold;
    color: ${theme?.colors.text || '#1f2937'};
    margin-bottom: 1rem;
    text-align: center;
  `,
  modalDescription: css`
    color: ${theme?.colors.text.secondary};
    margin-bottom: 2rem;
    text-align: center;
    line-height: 1.6;
  `,
  modalForm: css`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
  `,
  formGroup: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  modalLabel: css`
    font-size: 0.875rem;
    font-weight: 500;
    color: ${theme?.colors.text.secondary};
  `,
  modalInput: css`
    padding: 0.75rem;
    border: 1px solid ${theme?.colors.border};
    border-radius: 0.375rem;
    font-size: 1rem;
    background: ${theme?.colors.surface.layout.container};
    color: ${theme?.colors.text || '#111827'};
    &:focus {
      outline: none;
      border-color: ${theme?.colors.control.timetrack.progress};
    }
  `,

  modalActions: css`
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
  `,

  modalCancelButton: css`
    background-color: ${theme?.colors.surface.interactive.hover};
    color: ${theme?.colors.text.secondary};
    &:hover:not(:disabled) {
      background-color: ${theme?.colors.surface.interactive.hover};
    }
  `,
  modalSubmitButton: css`
    background-color: ${theme?.colors.control.timetrack.progress};
    color: white;
    &:hover:not(:disabled) {
      background-color: ${theme?.colors.control.timetrack.progress};
    }
  `,
  inputGroup: css`
    display: flex;
    align-items: start;
    gap: 0.5rem;
    width: 100%;
  `,
  verifiedMessage: css`
    display: flex;
    align-items: center;
    color: #16a34a;
    font-size: 0.875rem;
  `,

  // Header with title and language tabs
  header: css`
    display: flex;
    gap: 1rem;
    flex-direction: column;
  `,
  headerLanguageTabs: css`
    margin-left: auto;
  `,

  // Description text
  description: css`
    font-size: 0.9rem;
    color: ${theme?.colors.text.secondary};
    margin-bottom: 1.5rem;
  `,

  // Success/Info content area
  contentArea: css`
    padding: 2rem 0;
  `,
  contentAreaCentered: css`
    padding: 2rem 0;
    text-align: center;
  `,

  // Icon styles
  iconLarge: css`
    font-size: 3rem;
    margin-bottom: 1rem;
    text-align: center;
  `,

  // Message text
  messageText: css`
    font-size: 1rem;
    color: ${theme?.colors.text.primary};
    line-height: 1.8;
    margin-bottom: 2rem;
    text-align: center;
  `,
  messageTextSecondary: css`
    font-size: 1rem;
    color: ${theme?.colors.text.secondary};
    line-height: 1.6;
    margin-bottom: 2rem;
    text-align: center;
  `,

  // Info box
  infoBox: css`
    background-color: #f9fafb;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    text-align: center;
  `,
  infoBoxLabel: css`
    font-size: 0.9rem;
    color: ${theme?.colors.text.secondary};
    margin-bottom: 1rem;
  `,
  infoBoxHint: css`
    font-size: 0.8rem;
    color: ${theme?.colors.text.secondary};
    margin-top: 1rem;
  `,

  // Email link button
  emailLink: css`
    display: inline-block;
    padding: 0.75rem 1.5rem;
    background-color: white;
    border-radius: 6px;
    font-weight: 600;
    color: ${theme?.colors.button.bg.primary.default};
    text-decoration: none;
    transition: all 0.2s;
    border: 2px solid ${theme?.colors.button.bg.primary.default};
    cursor: pointer;

    &:hover {
      background-color: ${theme?.colors.button.bg.primary.hover};
      color: white;
    }
  `,

  // Logo
  logo: css`
    width: 360px;
    height: auto;
    color: white;
    filter: brightness(0) invert(1);
  `,
});
